import { CaretDown, CaretUp, Sparkle } from '@phosphor-icons/react';
import * as React from 'react';

import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { ButtonGroup } from '../ui/button-group';

import { cn } from '@/lib/utils';
import { pluralise } from '@/lib/pluralise';

export interface ArticleSuggestionsBannerProps extends React.HTMLAttributes<HTMLDivElement> {
  suggestionsCount: number;
  currentArticleIndex: number;
  totalArticles: number;
  onNextArticle: () => void;
  onPreviousArticle: () => void;
  onStartReviewSuggestions: () => void;
  onRejectAllSuggestions: () => void;
  onApproveAllSuggestions: () => void;
}

export const ArticleSuggestionsBanner = React.forwardRef<HTMLDivElement, ArticleSuggestionsBannerProps>(
  function ArticleSuggestionsBanner(
    {
      suggestionsCount,
      currentArticleIndex,
      totalArticles,
      onNextArticle,
      onPreviousArticle,
      onStartReviewSuggestions,
      onRejectAllSuggestions,
      onApproveAllSuggestions,
      className,
      ...props
    },
    ref,
  ) {
    const hasNextArticle = currentArticleIndex < totalArticles - 1;
    const hasPreviousArticle = currentArticleIndex > 0;
    const currentArticle = currentArticleIndex + 1;

    return (
      <div ref={ref} className={cn('flex bg-blue-200 rounded-md border border-blue-600', className)} {...props}>
        <div className="flex-1 py-3 px-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkle size={32} weight="fill" className="text-blue-700" />
            <p className="text-sm text-foreground">{pluralise(suggestionsCount, 'way')} to level up this article.</p>
          </div>
          <ButtonGroup orientation="horizontal" rounded={false} aria-label="Suggestion actions">
            <Button variant="link" onClick={onStartReviewSuggestions}>
              Start review
            </Button>
            <Button variant="link" onClick={onRejectAllSuggestions}>
              Reject all
            </Button>
            <Button variant="surface" elevation="shadow" onClick={onApproveAllSuggestions}>
              Approve all
            </Button>
          </ButtonGroup>
        </div>
        <div className="py-3 px-6 flex items-center bg-blue-300 border-l border-blue-700 gap-2 rounded-r-md">
          <p className="text-sm text-foreground">
            <span aria-hidden="true">
              {currentArticle}/{totalArticles}
            </span>
            <span className="sr-only" aria-live="polite">
              Article {currentArticle} of {totalArticles}
            </span>
          </p>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="link"
                size="icon"
                disabled={!hasNextArticle}
                onClick={onNextArticle}
                aria-label="Go to next article"
              >
                <CaretDown aria-hidden="true" weight="bold" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" bgColor="--bfc-foreground" className="text-background">
              Next Article
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="link"
                size="icon"
                disabled={!hasPreviousArticle}
                onClick={onPreviousArticle}
                aria-label="Go to previous article"
              >
                <CaretUp aria-hidden="true" weight="bold" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" bgColor="--bfc-foreground" className="text-background">
              Previous Article
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  },
);
